/**
 * App-wide safety net for the Radix "stranded body pointer-events" bug.
 *
 * Radix modal layers (Dialog, AlertDialog, Sheet, and modal Popover/Select/
 * DropdownMenu/ContextMenu) set `document.body.style.pointerEvents = "none"`
 * while open and restore it on close, via a single shared module-level layer
 * Set in @radix-ui/react-dismissable-layer. When two stacked layers (e.g. an
 * inner confirmation AlertDialog nested inside an outer Dialog) tear down in
 * overlapping ticks, that size-gated restore can misfire and leave
 * `pointer-events: none` stranded on <body> — freezing the entire page until a
 * manual refresh. It is an intermittent, timing-/browser-dependent race.
 *
 * These helpers detect and recover from the stranded state deterministically:
 * a stranded `none` is, by definition, `pointer-events: none` on <body> while
 * no Radix modal layer is actually open. See {@link usePointerEventsGuard} for
 * the wiring that runs this on the relevant DOM mutations.
 */

// Selector covering every Radix layer that legitimately disables outside pointer
// events (and thus freezes <body>) while it is mounted — open OR mid
// exit-animation. While any of these is present we must NOT touch body, because
// the `none` is intentional. Tooltip/HoverCard are non-modal and never freeze
// the body, so they are deliberately excluded.
const OPEN_RADIX_LAYER_SELECTOR = [
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[role="menu"]',
  '[role="listbox"]',
  "[data-radix-popper-content-wrapper]",
].join(",");

/** True when a Radix modal layer that freezes the body is mounted in the DOM. */
export function hasOpenRadixLayer(doc: Document = document): boolean {
  return doc.querySelector(OPEN_RADIX_LAYER_SELECTOR) !== null;
}

/**
 * Clears a stranded `pointer-events: none` from <body> when no Radix modal
 * layer is open. Returns true if it actually cleared a stranded value.
 *
 * Safe to call on every DOM mutation: it early-returns unless <body> is
 * currently frozen, so the (cheap) layer query only runs while a freeze is in
 * effect.
 */
export function clearStrandedBodyPointerEvents(doc: Document = document): boolean {
  const body = doc.body;
  if (!body || body.style.pointerEvents !== "none") return false;
  // A layer is still mounted (open or animating closed) — the freeze is
  // intentional; leave it for Radix to restore.
  if (hasOpenRadixLayer(doc)) return false;
  body.style.pointerEvents = "";
  return true;
}
